import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import { logout, selectUser } from '@features/auth';
import { paths } from '@shared/config/paths';
import { cn } from '@shared/lib/cn';
import { Avatar } from '@shared/ui/avatar/Avatar';
import { Icon } from '@shared/ui/icon/Icon';
import type { IconName } from '@shared/ui/icon/Icon';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}

const mainNav: NavItem[] = [
  { to: paths.dashboard, label: 'Tableau de bord', icon: 'dashboard', end: true },
  { to: paths.receipts, label: 'Tickets et dépenses', icon: 'receipt' },
  { to: paths.analytics, label: 'Analyses', icon: 'chart' },
];

const secondaryNav: NavItem[] = [
  { to: paths.settings, label: 'Paramètres', icon: 'settings' },
  { to: paths.help, label: 'Aide', icon: 'help' },
];

function NavList({
  items,
  collapsed,
  onToggleSidebar,
  onNavigate,
}: {
  items: NavItem[];
  collapsed: boolean;
  onToggleSidebar?: () => void;
  onNavigate?: () => void;
}) {
  return (
    <ul className="flex flex-col gap-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.end}
            // En mode réduit, le libellé est masqué : l'icône seule suffit,
            // le label reste accessible via title (tooltip natif).
            title={collapsed ? item.label : undefined}
            aria-label={collapsed ? item.label : undefined}
            onClick={() => {
              // En mode réduit (desktop), un clic déplie la sidebar.
              if (collapsed) onToggleSidebar?.();
              // Sur petit écran, un clic sur un lien referme le menu.
              onNavigate?.();
            }}
            className={({ isActive }) =>
              cn(
                'group flex items-center overflow-hidden rounded-md py-2 text-[13px] font-medium whitespace-nowrap transition-colors',
                collapsed ? 'justify-center px-0' : 'gap-2.5 px-2.5',
                isActive
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900',
              )
            }
          >
            {({ isActive }) => (
              <>
                <Icon
                  name={item.icon}
                  size={17}
                  className={cn(
                    'shrink-0',
                    isActive ? 'text-emerald-600' : 'text-slate-400 group-hover:text-slate-500',
                  )}
                />
                <span
                  aria-hidden={collapsed || undefined}
                  className={cn(
                    'truncate transition-all duration-150',
                    collapsed ? 'w-0 opacity-0' : 'opacity-100',
                  )}
                >
                  {item.label}
                </span>
              </>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

/** Menu profil (compte / déconnexion), accessible clavier. */
function ProfileMenu({
  collapsed,
  ...props
}: {
  collapsed: boolean;
  onToggleSidebar?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const onToggleSidebar = props.onToggleSidebar;
  const displayName = user?.name ?? 'Utilisateur';
  const displayEmail = user?.email ?? '';

  const handleLogout = () => {
    setOpen(false);
    dispatch(logout());
    void navigate(paths.login, { replace: true });
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onEscape);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onEscape);
    };
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={collapsed ? `Profil de ${displayName}` : undefined}
        title={collapsed ? displayName : undefined}
        onClick={() => {
          setOpen((value) => !value);
          if (collapsed) onToggleSidebar?.();
        }}
        className={cn(
          'flex w-full items-center overflow-hidden rounded-md py-2 text-left whitespace-nowrap transition-colors hover:bg-slate-100',
          collapsed ? 'justify-center px-0' : 'gap-2.5 px-2',
        )}
      >
        <Avatar name={displayName} className="shrink-0" />
        <span
          aria-hidden={collapsed || undefined}
          className={cn(
            'flex min-w-0 flex-col transition-all duration-150',
            collapsed ? 'w-0 opacity-0' : 'flex-1 opacity-100',
          )}
        >
          <span className="truncate text-[13px] font-medium text-slate-900">{displayName}</span>
          <span className="truncate text-xs text-slate-500">{displayEmail}</span>
        </span>
        <Icon
          name="chevron-down"
          size={16}
          className={cn(
            'shrink-0 text-slate-400 transition-all duration-150',
            open && 'rotate-180',
            collapsed && 'w-0 opacity-0',
          )}
        />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute inset-x-0 bottom-full mb-2 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
        >
          {['Mon compte', 'Préférences'].map((label) => (
            <button
              key={label}
              type="button"
              role="menuitem"
              onClick={() => setOpen(false)}
              className="block w-full px-3.5 py-2 text-left text-[13px] text-slate-700 transition-colors hover:bg-slate-100"
            >
              <span>{label}</span>
              <span className="ml-2 text-[9px] text-slate-400">en dev...</span>
            </button>
          ))}
          <div aria-hidden="true" className="my-1 border-t border-slate-100" />
          <button
            type="button"
            role="menuitem"
            onClick={handleLogout}
            className="block w-full px-3.5 py-2 text-left text-[13px] font-medium text-red-600 transition-colors hover:bg-red-50"
          >
            Déconnexion
          </button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Barre latérale : logo, navigation principale et secondaire, profil.
 *
 * Repliable (desktop) : en mode `collapsed`, seules les icônes sont visibles
 * (les libellés restent accessibles via tooltip). La préférence est gérée
 * par le parent (AppLayout) et persistée.
 *
 * `onNavigate` est appelé à chaque clic sur un lien de navigation : le
 * parent l'utilise typiquement pour refermer le menu sur petit écran
 * (le comportement `collapsed`/desktop n'est pas affecté).
 */
export function Sidebar({
  collapsed = false,
  onToggleSidebar,
  onNavigate,
}: {
  collapsed?: boolean;
  onToggleSidebar?: () => void;
  onNavigate?: () => void;
}) {
  return (
    <aside
      className={cn(
        'flex h-full w-full shrink-0 flex-col overflow-hidden border-r border-slate-200 bg-white text-slate-600',
      )}
    >
      {/* Logo Recivio */}
      <div className="flex h-16 items-center border-b border-slate-200">
        <NavLink
          to={paths.dashboard}
          aria-label="Recivio — accueil"
          className="flex items-center"
          onClick={() => onNavigate?.()}
        >
          <img src="/logo.png" alt="Recivio" className="rounded-md" width={140} height={92} />

          {!collapsed && (
            <div className="flex flex-col">
              <span className="animate-fade-in text-[10px] leading-tight font-medium text-slate-300">
                Simple.
              </span>

              <span className="animate-fade-in animation-delay-150 text-[10px] leading-tight font-medium text-slate-400">
                Rapide.
              </span>

              <span className="animate-fade-in animation-delay-300 text-[10px] leading-tight font-medium text-slate-500">
                Intelligent.
              </span>
            </div>
          )}
        </NavLink>
      </div>

      <nav
        aria-label="Navigation principale"
        className={cn('flex-1 overflow-x-hidden overflow-y-auto py-3', collapsed ? 'px-2' : 'px-3')}
      >
        <NavList
          items={mainNav}
          collapsed={collapsed}
          onToggleSidebar={onToggleSidebar}
          onNavigate={onNavigate}
        />
        <p
          aria-hidden={collapsed || undefined}
          className={cn(
            'mt-6 mb-1.5 px-2.5 text-[11px] font-semibold tracking-wider whitespace-nowrap text-slate-400 uppercase transition-opacity duration-150',
            collapsed && 'opacity-0',
          )}
        >
          Général
        </p>
        <NavList
          items={secondaryNav}
          collapsed={collapsed}
          onToggleSidebar={onToggleSidebar}
          onNavigate={onNavigate}
        />
      </nav>

      <div
        className={cn(
          'flex border-t border-slate-200',
          collapsed ? 'justify-center p-2' : 'items-center p-3',
        )}
      >
        <div className={cn('min-w-0', !collapsed && 'flex-1')}>
          <ProfileMenu collapsed={collapsed} onToggleSidebar={onToggleSidebar} />
        </div>
      </div>
    </aside>
  );
}
